import { type FC, type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Boxes, Building2, LogOut, Moon, Radar, Shield, Sun } from 'lucide-react';
import { cn } from '@matrix-forge/ui/lib/utils';
import { readShellIdentity } from '@/utils/gatewayShell';
import { parseBannerHeightMessage, BANNER_OFFSET_STORAGE_KEY } from './bannerProtocol';

type Theme = 'dark' | 'light';

/**
 * A single app tile in the gateway shell. `path` is the same-origin path the
 * gateway proxies (e.g. `/studio`, `/fleet`, `/admin`); it becomes the iframe
 * location when selected. `key` is the stable app id used for test hooks.
 * `mono` is the app's monogram letter, rendered on the brand gradient — shown on
 * its launcher tile AND on the orb when active, so the corner reads as native.
 */
type ShellApp = {
  key: 'studio' | 'enterprise' | 'strikekit' | 'mero' | 'fleet';
  label: string;
  /**
   * The app's icon. REQUIRED — registering a route without one is what let the
   * launcher fall back to bare monogram letters, so every tile read as an
   * anonymous coloured square ("K", "M", "F") that told you nothing about the
   * app.
   *
   * Reuse the icon the app ALREADY owns elsewhere rather than picking a new
   * one; that is what keeps the launcher tile, the app's own chrome and any
   * nav entry showing the same mark. The current bindings all come from the
   * sidebar links these tiles replaced:
   *
   *   StrikeKit -> Shield    (was `<SideBarLinkConnected icon={Shield}>`)
   *   Mero      -> Bot       (was `<SideBarLinkConnected icon={Bot}>`)
   *   Fleet     -> Boxes     (was the Construct Fleet sidebar link, now removed)
   *   Enterprise-> Building2 (matches the Overview tab's hero-building-office)
   *
   * If you add a tile, add its icon here and use the SAME component wherever
   * else the app is represented.
   */
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  /** Monogram fallback, and the app's short id in dense/test contexts. */
  mono: string;
  path: string;
  /**
   * Feature flag this tile's target route enforces. Omitted for Studio, the
   * always-present base app. Keeping the flag on the tile is what lets tile
   * visibility equal route accessibility.
   */
  feature?: string;
  /**
   * True when the route ALSO requires an admin role, not just the flag — i.e.
   * its pipeline runs `MatrixAuth.Plugs.RequireRoles`. Gated on `ADMIN_ROLES`,
   * which is that plug's vocabulary.
   */
  requiresAdmin?: boolean;
};

/** Strike48 brand gradient — matches Fleet's monogram (blue → gold). */
const BRAND_GRADIENT = 'linear-gradient(135deg, #3978d5, #efbf04)';

/**
 * The rail — and the ONLY tile list there is. There is no server-side mirror to
 * check yourself against: the gateway serves no launcher (see
 * `MatrixGateway.RootController`), so each entry's guards have to be read off
 * the route it points at, in `MatrixStudio.Router`. The nearest server-side
 * relative is `@shell_framed_prefixes` in `MatrixGateway.DynamicDispatch`, which
 * decides which top-level navigations get framed by this shell and names
 * `visibleApps` as the invariant it shares — it covers prefixes, not tiles, so it
 * is not a copy of this list.
 *
 *   Enterprise  -> the organization LiveView, gated on `organization_management`.
 *                  Replaces the old Admin Console tile. FEATURE-gated, not
 *                  role-gated, because that is exactly what Studio's
 *                  `:organization_browser` pipeline enforces — role-gating it
 *                  would hide it from a non-default role an admin had granted
 *                  the feature to.
 *   StrikeKit   -> `strikekit_enabled`
 *   Mero        -> `merovingian_enabled` (Merovingian)
 *   Fleet       -> `external_connectors` AND an admin role (`ADMIN_ROLES`), the
 *                  two guards on `:construct_admin_browser`.
 */
const SHELL_APPS: ShellApp[] = [
  { key: 'studio', label: 'Agentic SOC', icon: Radar, mono: 'S', path: '/studio' },
  {
    key: 'enterprise',
    label: 'Enterprise',
    icon: Building2,
    mono: 'E',
    path: '/studio/embed/organization',
    feature: 'organization_management',
  },
  {
    key: 'strikekit',
    label: 'StrikeKit',
    icon: Shield,
    mono: 'K',
    path: '/studio/strikekit',
    feature: 'strikekit_enabled',
  },
  {
    key: 'mero',
    label: 'Mero',
    icon: Bot,
    mono: 'M',
    // The tenant-root prefix, NOT `/studio/mero`.
    //
    // Mero is a LiveView app whose own chrome links to itself with root-relative
    // hrefs — the sidebar brand `<a href="/mero">MEROVINGIAN</a>`, every nav
    // item (`/mero/personas`, `/mero/sessions`, …), `push_navigate(to:
    // "/mero?new=1")`. Framing `/studio/mero` meant the very first click on
    // mero's own header navigated the top window to `/mero`, which the gateway
    // did not route: `{"error":"no_route","path":"/mero"}`.
    //
    // `MatrixStudio.MeroGatewayRoutes` now registers `/mero` as a real prefix,
    // so pointing the tile at the path mero already believes it lives at makes
    // the app's links and this tile agree instead of fighting.
    path: '/mero',
    feature: 'merovingian_enabled',
  },
  {
    key: 'fleet',
    label: 'Fleet',
    icon: Boxes,
    mono: 'F',
    // Studio's OWN fleet surface, not the standalone `/fleet` app.
    //
    // `/fleet` is served by fleet_platform, which is NOT deployed in local dev.
    // The gateway therefore 302s /fleet -> /fleet/auth/login?redirect=/fleet,
    // nothing answers that, and the browser gives up with "redirected you too
    // many times". A tile that always dead-ends is worse than no tile.
    //
    // `/studio/construct-fleet` is the in-Studio fleet UI, and it pipes through
    // `:construct_admin_browser` — authenticated + `RequireRoles` on
    // `MatrixAuth.Roles.admin_roles/0` + `RequireFeature "external_connectors"`.
    // That, not `fleet_in_studio`, is what the tile has to mirror to hold this
    // list's invariant ("tile visibility equals route accessibility").
    //
    // `fleet_in_studio` gates a DIFFERENT surface — `/studio/embed/fleet/pods`
    // (`FleetPodsLive`, the read-only pods list) via `:fleet_studio_browser`.
    // Keying the tile on it meant a tenant with `fleet_in_studio` off but
    // `external_connectors` on hid a tile whose route the operator could reach,
    // and a tenant with the reverse pair showed one that `RequireFeature` 404s.
    path: '/studio/construct-fleet',
    feature: 'external_connectors',
    requiresAdmin: true,
  },
];

/**
 * Mirrors `MatrixAuth.Roles.admin_roles/0` — the roster
 * `MatrixAuth.Plugs.RequireRoles` enforces on the routes these tiles point at.
 *
 * NOT `MatrixGateway.Auth.EffectiveRole.privileged_roles/0`, which this list used
 * to copy. That is the DE-ESCALATION set (which roles may voluntarily drop to a
 * lesser one), a different question from "may this user reach an admin route",
 * and the two rosters genuinely differ: it omits `tenant_admin` and includes
 * `mgmt_super_admin`. So every `tenant_admin` — the admin role every provisioned
 * child realm carries — was denied a tile whose route accepts them, while an
 * `mgmt_super_admin` was shown one `RequireRoles` then refuses.
 */
const ADMIN_ROLES = [
  'admin',
  'tenant_admin',
  'mssp_super_admin',
  'devo_super_admin',
  'strike48_super_admin',
  'super_admin',
];

/**
 * The tiles this session may actually reach. Fail-closed: a missing or
 * non-`true` feature hides the tile, exactly like the server's `feature_on?/2`,
 * so a user never sees an icon that leads to a 404.
 */
export function visibleApps(
  roles: string[],
  features: Record<string, unknown>,
): ShellApp[] {
  const isAdmin = roles.some((r) => ADMIN_ROLES.includes(r));
  return SHELL_APPS.filter((app) => {
    if (!app.feature) return true;
    if (features[app.feature] !== true) return false;
    return app.requiresAdmin ? isAdmin : true;
  });
}

const DEFAULT_APP_PATH = '/studio';
// The shell ONLY renders behind the gateway (isGatewayShellMode), and the
// gateway claims the whole `/auth` namespace — it owns the OIDC session, the
// session cookie and the Keycloak end-session redirect. Pointing logout at
// Studio's `/studio/auth/logout` asked the wrong app to end a session it does
// not own, so the gateway cookie survived and the Keycloak round-trip failed.
const LOGOUT_PATH = '/auth/logout';

/** The gateway-served bridge script we inject into each app iframe. */
const BRIDGE_SRC = '/gateway/shell-bridge.js';


/** Reads a `<meta name="...">` value injected by the gateway. */
function metaJson<T>(name: string, fallback: T): T {
  if (typeof document === 'undefined') return fallback;
  const raw = document.querySelector(`meta[name="${name}"]`)?.getAttribute('content');
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

// Mirrors the prefixes `MatrixGateway.Registry` actually routes. `pathFromLocation`
// uses it to decide whether the current URL is an app the shell can frame, so a
// prefix missing here means deep links and refreshes silently fall back to
// Studio — which is exactly what `/mero/personas` did before `/mero` was added.
const APP_PREFIX_RE = /^\/(studio|fleet|admin|mero)(?:\/|$)/;

/**
 * The tile for a full path, e.g. `/fleet/inventory` → `/fleet`.
 *
 * Matches the LONGEST tile path first. Enterprise and StrikeKit live *under*
 * `/studio`, so a plain `/(studio|fleet|admin)` match would collapse both to the
 * Studio tile and the orb badge would never show which of them you were actually
 * in. (Mero is a root prefix of its own now, so it no longer needs the
 * longest-match rule to be told apart from Studio — but Enterprise and StrikeKit
 * still do.)
 */
function appPathOf(fullPath: string): string {
  const pathname = fullPath.split(/[?#]/)[0];
  const match = [...SHELL_APPS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((a) => pathname === a.path || pathname.startsWith(`${a.path}/`));
  if (match) return match.path;
  const m = fullPath.match(APP_PREFIX_RE);
  return m ? `/${m[1]}` : DEFAULT_APP_PATH;
}

/** The app descriptor for a tile path, defaulting to Studio. */
function appOf(tilePath: string): ShellApp {
  return SHELL_APPS.find((a) => a.path === tilePath) ?? SHELL_APPS[0];
}

/**
 * The iframe path is the top-window location (`/fleet/inventory`, or for a
 * hash-routed app `/studio/#/documents`) — a clean, shareable, refreshable URL.
 * This works because the gateway serves the SHELL (not the Fleet app) for a
 * top-level document navigation to `/fleet` (it tells top-nav from the iframe's
 * own load via `Sec-Fetch-Dest`), and the Studio SPA's assets are absolute
 * `/studio/...` so they resolve at any URL. The hash is included so Studio's
 * HashRouter routes round-trip too. Falls back to the Studio tile when the
 * pathname isn't a known app.
 */
function pathFromLocation(): string {
  const full = window.location.pathname + window.location.search + window.location.hash;
  return APP_PREFIX_RE.test(window.location.pathname) ? full : DEFAULT_APP_PATH;
}

/** Write the iframe path into the top-window URL. `push` adds a history entry
 * (tile switches); replace updates in place (in-app navigation). Neither
 * `pushState` nor `replaceState` fires `popstate`, so there is no sync loop. */
function writePath(path: string, push: boolean): void {
  if (push) window.history.pushState(null, '', path);
  else window.history.replaceState(null, '', path);
}

function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/**
 * `BANNER_OFFSET_STORAGE_KEY` (imported from `bannerProtocol.ts`, the shared
 * contract) is the sessionStorage key the shell caches the last-known banner
 * height under, so a refresh mid-impersonation reopens with the orb already
 * offset instead of sliding down from the default position after the SPA
 * hydrates. Session storage is tab-scoped, which matches the impersonation
 * session's lifetime: closing the tab resets the guess, and the exit flow
 * clears it explicitly — `clearCachedBannerOffset` in the SPA on Exit to
 * Parent, or `writeCachedBannerOffset` here on receipt of height 0. Robust to
 * storage being blocked or serving a stale non-numeric value — always falls
 * back to 0.
 */
function readCachedBannerOffset(): number {
  try {
    const raw = window.sessionStorage.getItem(BANNER_OFFSET_STORAGE_KEY);
    if (!raw) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeCachedBannerOffset(height: number): void {
  try {
    if (height > 0) {
      window.sessionStorage.setItem(BANNER_OFFSET_STORAGE_KEY, String(Math.round(height)));
    } else {
      window.sessionStorage.removeItem(BANNER_OFFSET_STORAGE_KEY);
    }
  } catch {
    /* storage blocked */
  }
}

/** The signed-in operator's stored theme, else the shared app theme, else system.
 * Keyed by the JWT-derived user so the preference travels with the identity. */
function readStoredTheme(user: string): Theme {
  try {
    const mine = localStorage.getItem(`gateway:theme:${user}`);
    if (mine === 'dark' || mine === 'light') return mine;
    const shared = localStorage.getItem('phx:theme');
    if (shared === 'dark' || shared === 'light') return shared;
  } catch {
    /* storage blocked */
  }
  return systemTheme();
}

/** Force a theme onto a framed app's document — Studio reads the `.dark` class,
 * Fleet/Admin read `data-theme`, so we set both. */
function applyThemeToDoc(doc: Document | undefined | null, theme: Theme): void {
  const el = doc?.documentElement;
  if (!el) return;
  el.classList.toggle('dark', theme === 'dark');
  el.setAttribute('data-theme', theme);
  el.setAttribute('data-theme-source', 'user');
}

/**
 * GatewayShell — the top-level chrome rendered ONLY when the Studio SPA is
 * served through the separable gateway (see `utils/gatewayShell.ts`). A
 * full-bleed same-origin `<iframe>` pane with a single launcher **orb** pinned
 * top-left: the whole-site icon, which blooms into a big-icon app switcher
 * (Agentic SOC · Fleet · Admin). No persistent rail — the app owns the full
 * viewport; the orb is the only chrome, and its corner badge always shows which
 * app you're in. The gateway authenticates the origin once, so the iframed app
 * is already logged in.
 *
 * URL sync: the shell injects the gateway `shell-bridge.js` into each iframe on
 * load; the bridge posts the inner route up, and the shell mirrors it into the
 * top-window path (so the address bar tracks tile switches + in-app navigation).
 * Deep links / refresh restore the iframe from the path; back/forward drive it.
 * No app rewrites — one gateway-served script + this component + the gateway
 * serving the shell for top-level document navs to framed apps.
 */
export const GatewayShell: FC = () => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const initialPath = useRef<string>(pathFromLocation());
  const [activeApp, setActiveApp] = useState<string>(() => appPathOf(pathFromLocation()));
  const [menuOpen, setMenuOpen] = useState(false);

  // When the framed SPA is showing its tenant-impersonation banner (the amber
  // strip at the top of the iframe), it postMessages its measured height up so
  // we can push the orb down out of the banner's y-range. Without this the orb
  // stays at `top-4` and paints on top of the banner. See
  // `TenantImpersonationBanner.tsx` for the emitter side. (#3950)
  //
  // Seed initial state from `sessionStorage` so a page refresh (or a
  // navigation inside the same tab) starts at the correct offset — otherwise
  // the shell paints the orb at 16px, then jumps to 62-ish once the SPA
  // hydrates and posts, and the operator sees the orb slide down on every
  // load. The banner's mount-time post reconciles to the exact pixel height
  // even if the cached value is stale by a pixel or two. Cleared when the
  // banner reports height 0 (Exit to Parent), so a session that ends
  // impersonation doesn't stay left-padded on the next load.
  const [bannerOffset, setBannerOffset] = useState<number>(() => readCachedBannerOffset());
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Same-origin only: the framed SPA posts to `window.location.origin`,
      // never `"*"`. `parseBannerHeightMessage` (the shared `bannerProtocol`
      // contract the emitter builds through) returns null for anything that is
      // not a well-formed banner-height message, so unrelated/foreign messages
      // are ignored.
      if (e.origin !== window.location.origin) return;
      const h = parseBannerHeightMessage(e.data);
      if (h === null) return;
      setBannerOffset(h);
      writeCachedBannerOffset(h);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  // Distance from the top of the shell to the top of the orb: 1rem (16px) inset
  // below whatever the framed SPA is currently reserving at the top of the
  // viewport. The launcher panel anchors to the same origin so the two stay
  // visually attached when the orb moves.
  const orbTopPx = bannerOffset + 16;

  // Identity + theme are keyed to the JWT-authed operator (the shell is the
  // signed-in context), so the light/dark preference travels with the user.
  const identity = useMemo(() => readShellIdentity(), []);
  // Permission-derived rail: the gateway injects the verified roles/features
  // from the session JWT, so the tiles a user sees are the routes they can
  // actually reach. Fail-closed when the metas are absent.
  const apps = useMemo(
    () =>
      visibleApps(
        metaJson<string[]>('gateway-roles', []),
        metaJson<Record<string, unknown>>('gateway-features', {})
      ),
    []
  );
  const themeUser = identity?.user ?? 'anon';
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme(themeUser));
  const themeRef = useRef(theme);
  themeRef.current = theme;

  // Force the operator's theme onto the currently framed app's document.
  const applyTheme = useCallback((t: Theme) => {
    try {
      applyThemeToDoc(frameRef.current?.contentWindow?.document, t);
    } catch {
      /* cross-origin — shouldn't happen under the gateway */
    }
  }, []);

  // Inject the bridge into the iframe on every load, and stamp the theme (with a
  // couple of retries to beat the app's own theme init that runs just after).
  const handleFrameLoad = useCallback(() => {
    try {
      const doc = frameRef.current?.contentWindow?.document;
      if (doc && !doc.getElementById('gateway-shell-bridge')) {
        const s = doc.createElement('script');
        s.id = 'gateway-shell-bridge';
        s.src = BRIDGE_SRC;
        s.defer = true;
        (doc.head ?? doc.documentElement).appendChild(s);
      }
    } catch {
      // cross-origin (shouldn't happen under the gateway) — bridge unavailable.
    }
    [0, 200, 500].forEach((d) => window.setTimeout(() => applyTheme(themeRef.current), d));
  }, [applyTheme]);

  // Drive the iframe to a path — tile switch / deep-link / back-forward. We are
  // same-origin, so navigate the frame DIRECTLY (robust even while the target
  // frame's bridge is still loading — e.g. rapid tile clicks). Fall back to the
  // bridge's postMessage channel only if same-origin access ever throws (a
  // hypothetical cross-origin future).
  const navigateFrame = useCallback((path: string) => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    try {
      const current = win.location.pathname + win.location.search + win.location.hash;
      if (current !== path) win.location.assign(path);
    } catch {
      win.postMessage({ source: 'gateway-shell', type: 'navigate', path }, window.location.origin);
    }
  }, []);

  // Inner route → mirror into the top-window path (address bar tracks nav).
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { source?: string; type?: string; path?: string } | null;
      if (!d || d.source !== 'gateway-shell-bridge' || d.type !== 'route' || typeof d.path !== 'string') {
        return;
      }
      const current = window.location.pathname + window.location.search + window.location.hash;
      if (d.path !== current) writePath(d.path, false);
      setActiveApp(appPathOf(d.path));
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Top back/forward → drive the iframe to the URL's path.
  useEffect(() => {
    function onPopState() {
      const path = pathFromLocation();
      setActiveApp(appPathOf(path));
      navigateFrame(path);
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigateFrame]);

  const selectApp = useCallback(
    (app: string) => {
      setMenuOpen(false);
      if (app === appPathOf(pathFromLocation())) return;
      writePath(app, true);
      setActiveApp(app);
      navigateFrame(app);
    },
    [navigateFrame]
  );

  const handleLogout = useCallback(() => {
    (window.top ?? window).location.href = LOGOUT_PATH;
  }, []);

  const toggleTheme = useCallback(() => {
    const next: Theme = themeRef.current === 'dark' ? 'light' : 'dark';
    try {
      // Per-operator (travels with the JWT identity) + the shared `phx:theme`
      // key that Fleet/Admin honour on load and via cross-iframe storage events.
      localStorage.setItem(`gateway:theme:${themeUser}`, next);
      localStorage.setItem('phx:theme', next);
    } catch {
      /* storage blocked */
    }
    setTheme(next);
    applyTheme(next);
  }, [themeUser, applyTheme]);

  // Keep the framed app AND the shell's own window in the chosen theme, so the
  // launcher's own `dark:` styles track the light/dark preference too.
  useEffect(() => {
    applyTheme(theme);
    try {
      document.documentElement.classList.toggle('dark', theme === 'dark');
    } catch {
      /* no document */
    }
  }, [theme, applyTheme]);

  // Keyboard: Esc closes; `L` toggles the launcher (unless typing in a field).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        return;
      }
      if (e.key === 'l' || e.key === 'L') {
        const el = document.activeElement;
        const tag = el?.tagName;
        const typing =
          tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement | null)?.isContentEditable;
        if (!typing) setMenuOpen((o) => !o);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const active = appOf(activeApp);

  return (
    <div
      data-testid="gateway-shell"
      className="relative h-screen w-screen overflow-hidden bg-background text-foreground"
    >
      <main className="absolute inset-0 bg-background">
        <iframe
          ref={frameRef}
          title="Application"
          data-testid="gateway-shell-frame"
          src={initialPath.current}
          onLoad={handleFrameLoad}
          className={cn(
            'h-full w-full border-0 transition-[filter] duration-300 motion-reduce:transition-none',
            menuOpen && 'blur-sm brightness-50'
          )}
        />
      </main>

      {/* Scrim — click anywhere off the panel to close. Sits above the iframe,
          below the panel + orb, so it also swallows stray iframe interactions. */}
      {menuOpen && (
        <button
          type="button"
          aria-label="Close launcher"
          data-testid="gateway-shell-scrim"
          onClick={() => setMenuOpen(false)}
          className="absolute inset-0 z-30 cursor-default bg-black/30"
        />
      )}

      {/* The launcher orb — the only persistent chrome, and (since Studio's own
          "Sign Out" moved out of the framed sidebar) the ONLY route to logout.
          The label therefore has to advertise more than app switching: an
          operator hunting for "sign out" has no other affordance on screen, and
          "Open app launcher" gives them no reason to click here. `title` carries
          the same hint on hover for sighted users, and names the `L` shortcut. */}
      <button
        type="button"
        data-testid="gateway-shell-orb"
        aria-label="Open app launcher and account menu — switch apps, change theme, sign out"
        title="Apps, theme and sign out (press L)"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
        // `top` moves with the framed app's reserved top strip (impersonation
        // banner today; any future banner via the same postMessage). The
        // Tailwind `top-4` class stays out — inline `top` wins so we can vary
        // it. `top` is NOT in the transition list on purpose: animating the
        // shift makes the orb visibly slide down every time the operator
        // enters impersonation, which reads as sluggish. Snapping instantly
        // (combined with the sessionStorage-seeded initial position above)
        // keeps the transform-only hover / press animations intact.
        style={{ background: BRAND_GRADIENT, top: `${orbTopPx}px` }}
        className={cn(
          'absolute left-4 z-40 grid h-12 w-12 place-items-center rounded-xl',
          'border border-white/20 shadow-lg outline-none',
          'transition-transform duration-200 hover:scale-105 active:scale-95',
          // Hugging focus ring (no offset) so it reads the same on a light or
          // dark app sidebar — the offset ring flashed a harsh white gap on Studio.
          'focus-visible:ring-2 focus-visible:ring-[#5b8cff]/60 motion-reduce:transition-none',
          menuOpen && 'scale-95'
        )}
      >
        {/* The orb wears the CURRENT app's ICON on the brand gradient, so the
            corner reads as native to each page. It's still the launcher.
            `data-app` carries the monogram so tests can assert which app the
            orb is showing without depending on an SVG's internals. */}
        <span
          data-testid="gateway-shell-orb-glyph"
          data-app={active.mono}
          className="grid place-items-center text-[#08101f]"
          aria-hidden="true"
        >
          <active.icon className="h-6 w-6" aria-hidden />
        </span>
      </button>

      {/* The bloom panel — big-icon app switcher, anchored to the orb origin.
          Light-first with dark: overrides so it tracks the operator's theme.
          Uses the SAME dynamic `top` as the orb so the menu stays anchored to
          the orb when the impersonation banner pushes the orb down. */}
      {menuOpen && (
        <div
          role="menu"
          aria-label="Applications"
          data-testid="gateway-shell-launcher"
          style={{ top: `${orbTopPx}px` }}
          className={cn(
            'absolute left-4 z-40 w-[27rem] max-w-[calc(100vw-2rem)] origin-top-left',
            'rounded-2xl border p-4 shadow-2xl backdrop-blur-md',
            'border-slate-200 bg-white text-slate-900',
            'dark:border-white/10 dark:bg-gradient-to-b dark:from-[#161d2e] dark:to-[#0e1320] dark:text-white'
          )}
        >
          <div className="mb-3.5 flex items-center gap-3 px-1">
            <span className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 bg-[#1e2a44] shadow-md dark:border-white/10">
              <span className="h-6 w-6 bg-[url('/assets/strike48-mark.svg')] bg-contain bg-center bg-no-repeat" />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight">Strike48</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">matrix · gateway</div>
            </div>
            <span className="ml-auto rounded-md border border-slate-200 px-2 py-1 font-mono text-[10px] text-slate-500 dark:border-white/10 dark:text-slate-400">
              esc
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2.5" role="none">
            {apps.map((app) => {
              const isActive = activeApp === app.path;
              return (
                <button
                  key={app.path}
                  type="button"
                  role="menuitem"
                  data-testid={`gateway-app-${app.key}`}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => selectApp(app.path)}
                  className={cn(
                    'relative flex flex-col items-center gap-3 rounded-xl border p-4',
                    'transition-transform duration-150 hover:-translate-y-0.5',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5b8cff]',
                    'motion-reduce:transition-none',
                    isActive
                      ? 'border-[#5b8cff] bg-[#5b8cff]/10 shadow-[0_0_0_1px_rgba(91,140,255,0.25)] dark:border-[#5b8cff]/60'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]'
                  )}
                >
                  <span
                    style={{ background: BRAND_GRADIENT }}
                    data-app={app.mono}
                    className="grid h-16 w-16 place-items-center rounded-2xl border border-black/10 text-[#08101f] shadow-lg dark:border-white/20"
                    aria-hidden="true"
                  >
                    <app.icon className="h-8 w-8" aria-hidden />
                  </span>
                  <span className="text-center text-[13px] font-semibold leading-tight text-slate-700 dark:text-slate-100">
                    {app.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-3.5 flex items-center border-t border-slate-200 pt-3.5 dark:border-white/10">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-gradient-to-br from-[#5b8cff] to-[#9b6bff] text-xs font-bold uppercase text-white">
                {(identity?.user ?? 'u').slice(0, 2)}
              </span>
              <div className="min-w-0 leading-tight">
                <div
                  data-testid="gateway-shell-user"
                  className="truncate text-sm font-semibold text-slate-900 dark:text-white"
                  title={identity?.user}
                >
                  {identity?.user ?? 'Signed in'}
                </div>
                <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {identity ? [identity.realm, identity.role].filter(Boolean).join(' · ') : '—'}
                </div>
              </div>
            </div>
            {/* The footer controls are `menuitem`s too. This panel is a
                `role="menu"`, and assistive tech walks a menu by its
                `menuitem` children — a plain <button> inside one is not
                guaranteed to be reached or announced. That was survivable
                while Studio's sidebar still carried its own "Sign Out"; now
                that the shell owns logout outright, an unannounced Log out
                button is a screen-reader user with no way out at all. */}
            <button
              type="button"
              role="menuitem"
              data-testid="gateway-shell-theme"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Dark mode — switch to light' : 'Light mode — switch to dark'}
              className={cn(
                'ml-auto inline-flex flex-none items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                'border-slate-200 text-slate-700 hover:bg-slate-100',
                'dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5b8cff]/60'
              )}
            >
              {theme === 'dark' ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
              {theme === 'dark' ? 'Dark' : 'Light'}
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid="gateway-shell-logout"
              // Studio's sidebar control was worded "Sign Out"; this one reads
              // "Log out". With Studio's removed, the tooltip keeps the wording
              // the operator learned findable, without desyncing the accessible
              // name from the visible label (WCAG 2.5.3).
              title="Log out (sign out) — ends the gateway session for every app"
              onClick={handleLogout}
              className={cn(
                'inline-flex flex-none items-center gap-2 rounded-lg border border-[#f4868a]/30 px-3 py-1.5',
                'text-xs font-semibold text-[#f4868a] transition-colors hover:bg-[#f4868a]/10',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4868a]/50'
              )}
            >
              <LogOut className="h-3.5 w-3.5" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
